import React, { FC, memo, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import BasicImage from '@home/components/BasicImage';
import type { ISkillData } from '../../../types';
import { SKILL_DETAIL_SECTION_ICONS } from '../../../constants/skillDetail';
import { getRecommendSkillLabel, getRecommendSkills } from '../../../utils/recommendSkills';
import './index.scss';
import EllipsisHover from '@gp/components/EllipsisHover';

/**
 * 详情页推荐 Skill 列表 Props（Figma: 323×164）
 */
export interface ISkillRecommendListProps {
  /** 全量 Skill 列表（用于筛选同分类推荐） */
  allSkills: ISkillData[];
  /** 当前 Skill 唯一标识（排除自身） */
  currentSkillId: string;
  /** 是否英文环境 */
  isEN: boolean;
  /** Skill 点击回调 */
  onSkillClick: (skillId: string) => void;
}

const PREFIX = 'dba-skill-recommend-list';

/**
 * 详情页推荐 Skill 卡片（Figma: 323×164）
 */
const SkillRecommendList: FC<ISkillRecommendListProps> = ({
  allSkills,
  currentSkillId,
  isEN,
  onSkillClick,
}) => {
  const { formatMessage } = useIntl();
  const [hoveredSkillId, setHoveredSkillId] = useState<string | null>(null);

  const recommendSkills = useMemo(
    () => getRecommendSkills(allSkills, currentSkillId),
    [allSkills, currentSkillId],
  );

  if (recommendSkills.length === 0) {
    return null;
  }

  return (
    <aside className={PREFIX}>
      <div className={`${PREFIX}__title-bar`}>
        <BasicImage
          src={SKILL_DETAIL_SECTION_ICONS.linkageRobot}
          alt=""
          className={`${PREFIX}__title-icon`}
        />
        <span className={`${PREFIX}__title-text`}>
          {formatMessage({ id: 'dba_skill_detail_recommend_title' })}
        </span>
      </div>

      <div className={`${PREFIX}__list`}>
        {recommendSkills.map((item) => {
          const isHovered = hoveredSkillId === item.skill_id;
          return (
            <button
              key={item.skill_id}
              type="button"
              className={classNames(`${PREFIX}__item`, {
                [`${PREFIX}__item--hover`]: isHovered,
              })}
              onClick={() => onSkillClick(item.skill_id)}
              onMouseEnter={() => setHoveredSkillId(item.skill_id)}
              onMouseLeave={() => setHoveredSkillId(null)}
            >
              <span className={`${PREFIX}__item-dot`} />
              <span className={`${PREFIX}__name`}>
                <EllipsisHover
                  text={getRecommendSkillLabel(item, isEN)}
                />
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
};

export default memo(SkillRecommendList);
